import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Editor, EditorState, convertFromRaw } from 'draft-js';
import DOMPurify from 'dompurify';
import 'draft-js/dist/Draft.css';
import {
  makeStyles, tokens, Button, Link as FluentLink,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { API_BASE } from '../config.js';
import { iconMap } from '../theme/icons';
import { goldPalette } from '../theme/brand';

// labelKey — žr. t() naudojimą komponento viduje (žemiau); statinis `label`
// paliktas kaip atsarginė reikšmė, jei prireiktų naudoti šį masyvą be t().
const LINKS = [
  { slug: 'kokybes-garantija', label: 'Kokybės garantija', labelKey: 'topbar.quality_guarantee', icon: 'verified' },
  { slug: 'pinigu-grazinimas', label: '100% pinigų grąžinimas', labelKey: 'topbar.money_back', icon: 'refresh' },
  { slug: 'isimoketina-pirkimas', label: 'Pirkimas išsimokėtinai', labelKey: 'topbar.installment', icon: 'credit_card' },
  { slug: 'meistras-i-namus', label: 'Meistras į namus', labelKey: 'topbar.carpenter', icon: 'home_repair_service' },
];

const RIGHT_LINKS = [
  { slug: 'dovanu-cekis', label: 'Dovanų čekis', labelKey: 'topbar.gift_card', icon: 'card_giftcard' },
];

const RIGHT_NAV_LINKS = [
  { to: '/issirinkt-plokstes', label: 'Išsirink plokštes', labelKey: 'topbar.choose_panels', icon: 'palette' },
];

const CloseIcon = iconMap.close;

// Šių informacinių puslapių turinys (seedTextPages.js) prasideda <h1>, kuris
// dubliuoja DialogTitle jau rodomą pavadinimą — pašalinama, kad dialoge
// nebūtų dviejų vienodų antraščių.
const stripDuplicateH1 = (html) => html.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, '');

// Varnelės SVG kairėje yra dekoratyvi, prekės ženklo "gold" spalva (ne semantinis
// Fluent token'as, žr. theme/brand.js — goldPalette naudojamas tiesiogiai, kaip ir
// NavigationBar.jsx/primitives.jsx). Likę tarpai/spalvos/šriftas naudoja tokens.*.
const useStyles = makeStyles({
  bar: {
    display: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '11px',
    '@media (min-width: 768px)': { display: 'block' },
  },
  inner: {
    maxWidth: '80rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    height: '30px',
    overflowX: 'auto',
    // Turi sutapti su puslapio turinio konteinerių paddingu (žr. #15).
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  left: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXL, flexShrink: 0,
  },
  // Realus Fluent <Link as="button"> jau turi spalvą/hover/tranziciją
  // (žr. #5) — čia tik dydis/tarpai, kad atitiktų kompaktišką top-bar juostą.
  checkLink: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
    color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap',
    fontSize: '11px',
    ':hover': { color: tokens.colorNeutralForeground1 },
  },
  checkIcon: {
    width: '11px', height: '11px', color: goldPalette[500], flexShrink: 0,
  },
  right: {
    display: 'none', alignItems: 'center', gap: tokens.spacingHorizontalL, flexShrink: 0,
    '@media (min-width: 1024px)': { display: 'flex' },
  },
  iconLink: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
    color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightRegular,
    whiteSpace: 'nowrap', textDecorationLine: 'none', cursor: 'pointer',
    backgroundColor: 'transparent', border: 'none', padding: 0,
    fontFamily: 'inherit', fontSize: '11px',
    transitionProperty: 'color', transitionDuration: tokens.durationNormal,
    ':hover': { color: tokens.colorNeutralForeground1, textDecorationLine: 'underline' },
  },
  iconLinkIcon: {
    fontSize: '12px', display: 'flex', lineHeight: 1,
  },
  dialogSurface: {
    maxWidth: '32rem',
  },
  content: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.625',
  },
  htmlContent: {
    '& p': { marginBottom: tokens.spacingVerticalS },
    '& p:last-child': { marginBottom: 0 },
    // Žalia varnelė vietoj numatytojo disc žymeklio — turinys ateina kaip
    // žalias RAW HTML iš DB (textpages), tad varnelė pridedama CSS ::before
    // trikais, neliečiant paties turinio (žr. /admin/bugs "list item butu su
    // zaliomis varnelemis").
    '& ul': { listStyleType: 'none', marginLeft: 0, paddingLeft: 0, marginBottom: tokens.spacingVerticalS },
    '& ol': { listStyleType: 'decimal', marginLeft: '1.25rem', marginBottom: tokens.spacingVerticalS },
    '& li': { marginBottom: tokens.spacingVerticalXS },
    '& ul > li': {
      position: 'relative', paddingLeft: '1.5rem',
      '::before': {
        content: '"✓"', position: 'absolute', left: 0, top: 0,
        color: tokens.colorBrandForeground2, fontWeight: tokens.fontWeightBold,
      },
    },
    '& strong': { fontWeight: tokens.fontWeightSemibold },
    '& a': { color: tokens.colorBrandForegroundLink, textDecorationLine: 'underline', textUnderlineOffset: '2px' },
  },
});

function TopBar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [editorState, setEditorState] = useState(null);
  const [plainHtml, setPlainHtml] = useState('');
  const [customLink, setCustomLink] = useState('');
  const [activeSlug, setActiveSlug] = useState('');
  const navigate = useNavigate();
  const s = useStyles();

  const fetchContent = async (slug, fallbackLabel) => {
    try {
      const res = await fetch(`${API_BASE}/textpages/${slug}`);
      if (!res.ok) return;
      const data = await res.json();
      setDialogTitle(data.title || fallbackLabel);
      setCustomLink(data.customLink || '');
      try {
        const parsed = JSON.parse(data.content);
        if (parsed && parsed.blocks) {
          setEditorState(EditorState.createWithContent(convertFromRaw(parsed)));
          setPlainHtml('');
        } else {
          setEditorState(null);
          setPlainHtml(data.content || '');
        }
      } catch {
        setEditorState(null);
        setPlainHtml(data.content || '');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpen = (slug, label) => { fetchContent(slug, label); setOpen(true); setActiveSlug(slug); };
  const handleClose = () => { setOpen(false); setEditorState(null); setPlainHtml(''); setCustomLink(''); setDialogTitle(''); setActiveSlug(''); };

  return (
    <>
      <div className={s.bar}>
        <div className={s.inner}>
          {/* Kairė: varnelės nuorodos */}
          <div className={s.left}>
            {LINKS.map(({ slug, labelKey }) => (
              <FluentLink as="button" key={slug} onClick={() => handleOpen(slug, t(labelKey))} className={s.checkLink}>
                <svg className={s.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {t(labelKey)}
              </FluentLink>
            ))}
          </div>
          {/* Dešinė: ikoninės nuorodos */}
          <div className={s.right}>
            {RIGHT_LINKS.map(({ slug, labelKey, icon }) => {
              const IconComponent = iconMap[icon];
              return (
                <FluentLink as="button" key={slug} onClick={() => handleOpen(slug, t(labelKey))} className={s.iconLink}>
                  <span className={s.iconLinkIcon}><IconComponent /></span>
                  {t(labelKey)}
                </FluentLink>
              );
            })}
            {RIGHT_NAV_LINKS.map(({ to, labelKey, icon }) => {
              const IconComponent = iconMap[icon];
              return (
                <Link key={to} to={to} className={s.iconLink}>
                  <span className={s.iconLinkIcon}><IconComponent /></span>
                  {t(labelKey)}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(e, data) => { if (!data.open) handleClose(); }}>
        <DialogSurface className={s.dialogSurface}>
          <DialogBody>
            <DialogTitle
              action={<Button appearance="subtle" icon={<CloseIcon />} onClick={handleClose} aria-label={t('topbar.close')} />}
            >
              {dialogTitle || t('topbar.info_fallback_title')}
            </DialogTitle>
            <DialogContent className={s.content}>
              {editorState ? (
                <Editor editorState={editorState} readOnly onChange={() => {}} />
              ) : (
                <div className={s.htmlContent} dangerouslySetInnerHTML={{ __html: stripDuplicateH1(DOMPurify.sanitize(plainHtml)) }} />
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" size="large" onClick={handleClose}>{t('topbar.close')}</Button>
              {(customLink || activeSlug === 'dovanu-cekis') && (
                <Button
                  as="a"
                  href={customLink || '/dovanu-cekis'}
                  onClick={(e) => { if (!customLink) { e.preventDefault(); handleClose(); navigate('/dovanu-cekis'); } }}
                  appearance="primary"
                  size="large"
                >{t('topbar.order')}</Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

export default TopBar;
